import { FilteredCreditApplication, useFeatureFlags } from '@applyonline/shared'
import { EApplicantType, EDocumentStatus, EDocumentType, EExternalStep, EFinancingProgram } from '@if/core/types'
import { getEnvRuntimeVariable } from '@if/core/utils'
import { useTranslation } from 'react-i18next'
import { applicantFullName } from '../../../../../data/credit-application-selectors'
import CreditStep, { CreditStepStatus } from './CreditStep'

interface IdentityVerificationCreditStepProps {
  creditApp: FilteredCreditApplication
  applicantType: EApplicantType
}

const IdentityVerificationCreditStep = ({ creditApp, applicantType }: IdentityVerificationCreditStepProps) => {
  const { t } = useTranslation()
  const [flags] = useFeatureFlags(EFinancingProgram.Personal)
  const applicant = applicantType === EApplicantType.Applicant ? creditApp.applicant : creditApp.coapplicant!

  // Feature switch: the whole Personal thin-file Vouched flow is off unless this is on.
  if (!flags?.enableVouchedForThinCreditB2B) return null

  // Local dev: skip Vouched the same way B2C does
  if (!getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_VOUCHED)) return null

  // Thin-file trigger (B2B-only): the `identityVerification` RequiredExternalStep is the signal
  // that this applicant must complete Vouched. It only decides whether to show the step.
  const isVouchedRequired = creditApp.requiredExternalSteps.some(
    (x) => x.applicantType === applicantType && x.externalStepId === EExternalStep.IdentityVerification,
  )

  if (!isVouchedRequired) return null

  // Status is driven by the ValidPhotoId document Vouched produces — same document-centric
  // model as B2C: Approved unblocks payout; AwaitingApproval is pending agent review.
  const idDocument = creditApp.documents.find(
    (x) => x.typeId === EDocumentType.ValidPhotoId && x.applicantType === applicantType,
  )

  const status =
    idDocument?.status === EDocumentStatus.Approved
      ? CreditStepStatus.Completed
      : idDocument?.status === EDocumentStatus.AwaitingApproval
        ? CreditStepStatus.Await
        : CreditStepStatus.RequiresAttention

  const description =
    status === CreditStepStatus.Completed
      ? 'overview.vouchedConfirmed'
      : status === CreditStepStatus.Await
        ? 'overview.vouchedPending'
        : 'overview.vouchedVerify'

  return (
    <CreditStep
      status={status}
      linkToDetail={`${applicantType.toLowerCase()}-identity-verification`}
      hideLinkToDetail={status === CreditStepStatus.Completed}
      title={t('overview.vouchedStep', { name: applicantFullName(applicant) })}
      content={t(description)}
    />
  )
}

export default IdentityVerificationCreditStep
